import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ChevronsUpDown,
  Inbox,
  Plus,
  RefreshCw,
  Search,
  ShieldAlert,
  UserSearch,
  X,
} from "lucide-react";
import { ApiError } from "@/lib/api";
import { getPerson, type Person } from "@/lib/people";
import { BulkActionBar } from "@/components/BulkActionBar";
import { PriorityPill } from "@/components/PriorityPill";
import { UnreadBadge } from "@/components/UnreadBadge";
import { ColumnPicker } from "@/components/tickets/ColumnPicker";
import { TicketTabs } from "@/components/tickets/TicketTabs";
import { ColumnResizer } from "@/components/tickets/ColumnResizer";
import {
  countActiveFilters,
  MINIMUM_SEARCH_LENGTH,
  NoFilterMatches,
  QueueFilters,
  type QueueFilterValue,
} from "@/components/tickets/QueueFilters";
import { textValue, useTicketColumns } from "@/components/tickets/ticket-columns";
import {
  useIsAgent,
  useSession,
  useZone,
  type SessionTicketTabs,
} from "@/components/session-provider";
import { endOfDayExclusiveUtc, formatDateTime, startOfDayUtc } from "@/lib/time";
import {
  ageOf,
  createSavedView,
  deleteSavedView,
  getDashboard,
  getSavedViews,
  listTickets,
  unreadFor,
  statusLabel,
  slaTone,
  statusTone,
  savedViewToBaseQuery,
  type Dashboard,
  type SavedView,
  type TicketAttention,
  type TicketListQuery,
  type TicketPriority,
  type TicketSortField,
  type TicketStatus,
  type TicketSummary,
} from "@/lib/tickets";
import { cn } from "@/lib/utils";

/**
 * A dashed GUID, which is what every id in this app's URLs is.
 *
 * ⚠️ Shape only, and it is not a security check — the server decides what an id may reach,
 * and every filter narrows within the query filter and RLS regardless. It is here so a
 * mangled or truncated link produces the unfiltered queue rather than a 400 from model
 * binding, which reads as "the tickets page is broken".
 */
const GUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/** The server's six. See `TicketStatus` for what a phantom seventh cost. */
const STATUSES: readonly TicketStatus[] = [
  "New",
  "InProgress",
  "AwaitingUser",
  "OnHold",
  "Resolved",
  "Closed",
];

/** A calendar day in the reader's own zone, which is the only shape a date filter takes. */
const DAY = /^\d{4}-\d{2}-\d{2}$/;

const PRIORITIES: readonly TicketPriority[] = ["Low", "Normal", "High", "Urgent", "Critical"];

/** The server's `AttentionFilter`, member for member. Followed from a home tile, never typed. */
const ATTENTION: readonly TicketAttention[] = [
  "AwaitingDeskReply",
  "GoneQuiet",
  "AwaitingRating",
  "Unread",
];

const SORT_FIELDS: readonly TicketSortField[] = [
  "CreatedAt",
  "UpdatedAt",
  "Status",
  "Number",
  "Priority",
];

interface TicketsSearch {
  view?: string;
  requester?: string;

  /**
   * Set by following one of the home screen's attention tiles.
   *
   * ⚠️ Validated against a closed set here like every other value, so a mangled link yields
   * the unfiltered queue rather than a 400 — and refused outright by the server if it somehow
   * arrives anyway (D-60). The tile's count and this filter read the SAME predicate
   * server-side (`TicketAttention`), which is what stops a tile that says 3 opening a list of
   * 11.
   */
  attention?: TicketAttention;

  /**
   * The requester screen's tab.
   *
   * ⚠️ NOT `view`, deliberately. Two screens live on this route and `view` is the agent
   * queue's tab name — "All open", "My tickets". Sharing one parameter between two different
   * vocabularies means an agent following a requester's link falls back to "All open" and
   * silently gets an unfiltered queue, which is the exact defect these links exist to fix.
   */
  tab?: RequestFilter;

  /**
   * The requester screen's scope strip — their own requests, ones shared with them, ones
   * their reports raised (D-47).
   *
   * ⚠️ A flag resolved from the SESSION server-side, never an id, so this only ever names
   * which of the caller's own three relationships to apply. Forcing one somebody does not
   * have yields an empty page, not another person's rows.
   */
  scope?: RequestScope;
  status?: TicketStatus;
  priority?: TicketPriority;
  team?: string;
  site?: string;
  assignee?: string;

  /** Raised between these two days, inclusive, in the reader's own time zone. */
  from?: string;
  to?: string;

  /**
   * A subject search.
   *
   * ⚠️ `q`, not `search`, because `Route.useSearch()` already calls this whole object the
   * search — a member named `search.search` reads as a mistake every time anybody meets it.
   *
   * ⚠️ Bounded here as well as server-side. It is free text arriving from a pasted link, and
   * an over-long value is dropped rather than sent to be refused; the server still refuses
   * one that reaches it (D-60), so this only keeps a mangled URL from rendering an error.
   */
  q?: string;
  sort?: TicketSortField;
  dir?: "asc" | "desc";
}

/**
 * ⚠️ Every value is validated against a known set and dropped if it does not match.
 *
 * The search string is user input arriving from a pasted link, so nothing here is trusted:
 * an unrecognised status cannot become a status, and an id-shaped parameter has to look
 * like an id. This is not what makes the filters safe — the server ignores a filter the
 * caller may not use and applies the rest in SQL on top of RLS — it is what keeps a
 * mangled URL from rendering an error page.
 *
 * ⚠️ Ids only, never names. A colleague's name has no business in a link somebody pastes
 * into a chat window; the screen resolves each id to a name for display.
 */
function validateSearch(search: Record<string, unknown>): TicketsSearch {
  const pick = <T extends string>(value: unknown, allowed: readonly T[]): T | undefined =>
    typeof value === "string" && (allowed as readonly string[]).includes(value)
      ? (value as T)
      : undefined;

  const id = (value: unknown): string | undefined =>
    typeof value === "string" && GUID.test(value) ? value : undefined;

  const day = (value: unknown): string | undefined =>
    typeof value === "string" && DAY.test(value) ? value : undefined;

  const result: TicketsSearch = {
    ...(typeof search.view === "string" ? { view: search.view } : {}),

    // Set by the people screen's "view their requests". Passed straight to the API, which
    // ignores it unless the caller holds user.manage or it is their own id — and which can
    // only ever NARROW within the query scope and RLS, so it cannot surface a ticket they
    // could not read.
    ...(id(search.requester) ? { requester: id(search.requester) } : {}),
    ...(id(search.team) ? { team: id(search.team) } : {}),
    ...(id(search.site) ? { site: id(search.site) } : {}),

    // Same again: ignored server-side unless the caller holds ticket.assign (D-31).
    ...(id(search.assignee) ? { assignee: id(search.assignee) } : {}),

    // Shape only. A day that does not exist — 2026-02-31 — is dropped when the bounds are
    // computed rather than filtering to whatever the Date constructor rolls it over to.
    ...(day(search.from) ? { from: day(search.from) } : {}),
    ...(day(search.to) ? { to: day(search.to) } : {}),

    // Length only, matching the server's own bounds. Nothing about the CONTENT is checked,
    // because there is nothing to check — it is matched as a literal against one column with
    // LIKE wildcards escaped server-side, so there is no expression here to sanitise.
    ...(typeof search.q === "string" &&
    search.q.trim().length >= MINIMUM_SEARCH_LENGTH &&
    search.q.length <= 200
      ? { q: search.q.trim() }
      : {}),
  };

  const status = pick(search.status, STATUSES);
  const priority = pick(search.priority, PRIORITIES);
  const sort = pick(search.sort, SORT_FIELDS);
  const dir = pick(search.dir, ["asc", "desc"] as const);
  const attention = pick(search.attention, ATTENTION);
  const tab = pick(search.tab, REQUEST_FILTERS);
  const scope = pick(search.scope, REQUEST_SCOPES);

  if (attention) result.attention = attention;
  if (tab) result.tab = tab;
  if (scope) result.scope = scope;
  if (status) result.status = status;
  if (priority) result.priority = priority;
  if (sort) result.sort = sort;
  if (dir) result.dir = dir;

  return result;
}

export const Route = createFileRoute("/tickets/")({
  // Filters live in the URL so a narrowed queue can be bookmarked, shared with a colleague
  // and reached again with the browser's own back button — none of which works when a
  // filter is component state.
  validateSearch,
  component: TicketsRoute,
});

function TicketsRoute() {
  // The agent queue and "my requests" are two screens over one route. Which you get
  // follows from a permission, and the server scopes the rows either way — so a requester
  // who forces the agent view sees their own tickets in a wider table, not anyone else's.
  const isAgent = useIsAgent();
  return isAgent ? <AgentQueue /> : <MyRequests />;
}

/**
 * Loads a page of tickets.
 *
 * Every filter travels to the server. Fetching everything and narrowing in the browser is
 * how the mock leaked internal notes (defect D2), and it does not work here anyway: the
 * list is paged, so a client-side filter only ever searches the page it happens to hold.
 */
function useTickets(query: TicketListQuery) {
  const [items, setItems] = useState<TicketSummary[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const key = JSON.stringify(query);

  const load = useCallback(
    async (append: boolean, nextCursor?: string) => {
      setError(null);

      try {
        const page = await listTickets({
          ...query,
          cursor: nextCursor,
        });

        setItems((previous) => (append && previous ? [...previous, ...page.items] : page.items));
        setCursor(page.nextCursor);
        setHasMore(page.hasMore);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : "Could not load tickets.");
        if (!append) setItems([]);
      }
    },
    // query is compared by value; a new object each render would loop forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );

  useEffect(() => {
    setItems(null);
    void load(false);
  }, [load]);

  const loadMore = async () => {
    if (!cursor || isLoadingMore) return;
    setIsLoadingMore(true);
    await load(true, cursor);
    setIsLoadingMore(false);
  };

  return { items, error, hasMore, isLoadingMore, loadMore, reload: () => load(false) };
}

// ---------------------------------------------------------------------------
// Agent queue
// ---------------------------------------------------------------------------

const queueViews = {
  "All open": { openOnly: true },
  "My tickets": { openOnly: true, mine: true },
  Unassigned: { openOnly: true, unassigned: true },
  Urgent: { openOnly: true, minimumPriority: "Urgent" },

  // Active requests from VIPs. `openOnly` is what "active" means everywhere else on this
  // screen — not Resolved and not Closed — so the tab agrees with the other four rather
  // than inventing a sixth definition.
  //
  // ⚠️ The server decides who is a VIP and applies the filter in SQL on top of the query
  // filter and RLS. This tab therefore shows a subset of the rows the caller could already
  // list: it cannot surface a VIP's request to somebody whose scope does not reach it, and
  // VIP status is not consulted anywhere in that decision.
  VIP: { openOnly: true, vipOnly: true },

  // Requests somebody invited them onto (D-47).
  //
  // ⚠️ An agent needs this as much as a requester does, and arguably more: being added to
  // a colleague's ticket is how a second pair of eyes gets asked for, and before this the
  // only way to find one again was to remember its number. `openOnly` for the same reason
  // as every other tab here — an invitation onto a closed request is history, not work.
  "Shared with me": { openOnly: true, sharedWithMe: true },

  // Raised by their direct reports, and only those who agreed (D-46).
  "My team": { openOnly: true, myReports: true },

  All: {},
} as const satisfies Record<string, TicketListQuery>;

type QueueView = keyof typeof queueViews;

/**
 * Views that only exist when the person has the relationship behind them.
 *
 * ⚠️ Hidden rather than shown-and-empty, per D-47. Most people are on nobody's ticket and
 * manage nobody, and two permanently empty tabs on the busiest screen in the product are
 * two things to explain forever. An empty "My team" is the worse of the two: it reads as
 * "my reports have no open problems" when it means "nobody reports to you".
 *
 * ⚠️ Hiding a tab is not a control. Both are ordinary URLs, and forcing one resolves the
 * same server-side filter from the session — so the answer is an empty page, never
 * somebody else's rows.
 */
const relationshipViews = {
  "Shared with me": "hasSharedWithMe",
  "My team": "hasTeamRequests",
} as const satisfies Partial<Record<QueueView, keyof SessionTicketTabs>>;

/**
 * Which way a column sorts the first time you click it.
 *
 * Newest, most urgent and most recently touched first; numbers and statuses ascending,
 * because those read as a sequence. Getting this wrong is not a bug so much as one wasted
 * click on every single use.
 */
const firstDirection: Record<TicketSortField, "asc" | "desc"> = {
  CreatedAt: "desc",
  UpdatedAt: "desc",
  Priority: "desc",
  Status: "asc",

  // ⚠️ Descending, changed with the sort itself (D-88). Ascending was defensible while this
  // ordered the opaque number, where neither direction meant anything; now that it orders the
  // readable one, ascending opens the queue on request #1 — the oldest thing the desk has ever
  // been asked, which on a live queue is several thousand rows from anything anybody is
  // working on.
  Number: "desc",
};

function AgentQueue() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const view = (search.view && search.view in queueViews ? search.view : "All open") as QueueView;

  // ⚠️ Which tabs to DRAW, never which rows to return. A relationship view whose flag is
  // false is left out of the strip; the URL still works and still resolves server-side.
  const { user } = useSession();
  const tabs = user?.ticketTabs;

  // ⚠️ The reader's zone, not the browser's: it turns the day somebody picked into the pair
  // of instants the server compares. See `startOfDayUtc` in `@/lib/time`.
  const zone = useZone();

  const visibleViews = (Object.keys(queueViews) as QueueView[]).filter((v) => {
    const flag =
      v in relationshipViews ? relationshipViews[v as keyof typeof relationshipViews] : null;

    // ⚠️ Keeps the CURRENT view visible even when its flag is false, so a bookmarked or
    // shared "Shared with me" link does not render a strip with no selected tab and no way
    // back to the one you are looking at.
    return flag === null || v === view || tabs?.[flag] === true;
  });

  const [saved, setSaved] = useState<SavedView[]>([]);

  // Naming a view, in the page rather than in an OS dialog.
  //
  // ⚠️ `window.prompt` and `window.alert` were the only two native dialogs left in the
  // product — every destructive action in administration already asks in the page — and they
  // were the wrong tool here for a reason beyond consistency: the prompt carried the warning
  // about which filters cannot be saved, in an unstyled box some browsers suppress outright,
  // and the alert reported a duplicate name after throwing away the name that caused it.
  const [namingView, setNamingView] = useState(false);
  const [viewName, setViewName] = useState("");
  const [savingView, setSavingView] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  /** Which saved view has been asked about, so the strip can put the question in its place. */
  const [deletingView, setDeletingView] = useState<string | null>(null);

  // Column layout is a per-person display preference, not an access decision. See
  // ticket-columns.ts for why localStorage is appropriate here and was not for D1.
  const { columns, widthOf, toggle, resize, reset, visibleIds } = useTicketColumns();
  const [savedId, setSavedId] = useState<string | null>(null);

  // Ticket NUMBERS, not ids — the opaque public identifier is what every endpoint takes,
  // and the list response deliberately carries no internal id to select by.
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    void getSavedViews()
      .then(setSaved)
      .catch(() => setSaved([]));
  }, []);

  const activeSaved = saved.find((s) => s.id === savedId) ?? null;

  const filters: QueueFilterValue = {
    ...(search.attention ? { attention: search.attention } : {}),
    ...(search.status ? { status: search.status } : {}),
    ...(search.priority ? { minimumPriority: search.priority } : {}),
    ...(search.team ? { teamId: search.team } : {}),
    ...(search.site ? { siteId: search.site } : {}),
    ...(search.assignee ? { assigneeUserId: search.assignee } : {}),
    ...(search.requester ? { requesterUserId: search.requester } : {}),
    ...(search.from ? { raisedFrom: search.from } : {}),
    ...(search.to ? { raisedTo: search.to } : {}),
    ...(search.q ? { search: search.q } : {}),
  };

  const activeFilters = countActiveFilters(filters);

  /**
   * Filters in effect that a saved view has nowhere to store.
   *
   * ⚠️ Said BEFORE the name is typed rather than discovered later, when the view silently
   * returns a wider queue than the one it was saved from. Derived here rather than inside the
   * save, because the warning belongs on screen while somebody is deciding — which is
   * precisely what it could not do while it lived inside a `window.prompt` string.
   */
  const unsaveableFilters = [
    filters.assigneeUserId ? "assignee" : null,
    filters.requesterUserId ? "requester" : null,
    filters.raisedFrom || filters.raisedTo ? "date" : null,
    filters.attention ? "attention" : null,
  ].filter((f): f is string => f !== null);

  /** Writes the filter set back to the URL. Replaces, so filtering is not a history trail. */
  const setFilters = (next: QueueFilterValue) =>
    void navigate({
      search: (current: TicketsSearch) => ({
        ...current,
        attention: next.attention,
        status: next.status,
        priority: next.minimumPriority,
        team: next.teamId,
        site: next.siteId,
        assignee: next.assigneeUserId,
        requester: next.requesterUserId,
        from: next.raisedFrom,
        to: next.raisedTo,
        q: next.search,
      }),
      replace: true,
    });

  // ⚠️ Named on screen. A filter that silently shortens the queue is worse than no filter:
  // somebody arrives from the people screen, sees six tickets where there were sixty, and
  // has no way to tell a filter from an outage. Fetched by id rather than passed through
  // the URL, because a person's name does not belong in a link that gets pasted around.
  //
  // ⚠️ THREE STATES, NOT TWO. `undefined` means the lookup has not answered yet and `null`
  // means it refused — and the difference is what stops this banner making a claim the
  // server did not honour. See the banner itself for why that mattered.
  const [filteredPerson, setFilteredPerson] = useState<Person | null | undefined>(undefined);

  useEffect(() => {
    if (!search.requester) {
      setFilteredPerson(undefined);
      return;
    }

    let cancelled = false;

    void getPerson(search.requester)
      .then((p) => !cancelled && setFilteredPerson(p))
      .catch(() => !cancelled && setFilteredPerson(null));

    return () => {
      cancelled = true;
    };
  }, [search.requester]);

  /**
   * Whether the requester filter in the URL is one the SERVER will actually apply.
   *
   * ⚠️ It accepts the filter for your own id or for a holder of `user.manage`, and otherwise
   * drops it silently — a deliberate decision, because a 403 from a list endpoint over a
   * display filter is a puzzle rather than a correction. The consequence landed here: this
   * screen drew "Showing only requests raised by …" on the mere presence of the parameter, so
   * an agent following a shared link saw that sentence over a completely unfiltered queue.
   * And because the name lookup is fenced to the same permission, it read "raised by one
   * person" — a claim about their own data that was simply untrue.
   *
   * Own id needs no lookup to be sure of, which is also the case the home screen's own tile
   * relies on.
   */
  const filteringOwnRequests = Boolean(search.requester) && search.requester === user?.userId;
  const requesterFilterApplied = filteringOwnRequests || filteredPerson != null;

  // A saved view replaces the built-in view entirely rather than merging with it — half of
  // one view and half of another is a queue nobody asked for.
  //
  // ⚠️ Only its tab-like flags come from here. Its team, site, status and priority are
  // seeded into the URL when the view is selected, so the filter bar states everything
  // that is actually being applied and every chip clears the thing it names.
  const base: TicketListQuery = activeSaved ? savedViewToBaseQuery(activeSaved) : queueViews[view];

  // ⚠️ NUMBER DESCENDING by default (D-88), where this was Priority descending.
  //
  // Priority-first is the textbook answer and it is not what a desk asked for: an agent
  // returning to the queue wants the newest requests, and "#412, #411, #410" is an ordering
  // anybody can verify at a glance — where priority-first reorders itself as triage happens,
  // so the row that was third is somewhere else a minute later. Urgency has not been thrown
  // away: the SLA dot and label ride on every row, the priority column still sorts, and the
  // saved views carry their own ordering.
  //
  // ⚠️ This became a reasonable default only because the sort itself was fixed. Number sort
  // previously ordered the opaque Crockford string while the column rendered #412, so making
  // it the default beforehand would have opened every queue in a random order.
  //
  // The URL is the single source of truth for the ordering, including a saved view's —
  // selecting one seeds its sort here. Two places deciding the sort is how a column header
  // ends up appearing to do nothing.
  const sortBy: TicketSortField = search.sort ?? "Number";

  const direction: "Ascending" | "Descending" =
    (search.dir ?? firstDirection[sortBy]) === "asc" ? "Ascending" : "Descending";

  // ⚠️ Built field by field rather than spread, because two of the filters do not cross the
  // wire in the shape the screen holds them: a day belongs to the reader's time zone and the
  // server compares instants. Spreading would send `raisedFrom=2026-08-12`, which the
  // endpoint has no parameter for — a filter the chips say is applied and the query never
  // carries, which is the one failure this file's comments are about.
  const query: TicketListQuery = {
    ...base,

    // ⚠️ AN ATTENTION BUCKET REPLACES THE VIEW'S OWN OPEN/CLOSED NARROWING, rather than being
    // intersected with it. Every built-in view carries `openOnly`, and each bucket already
    // defines its own scope server-side — two of them narrow to open, "waiting for your
    // feedback" narrows to Resolved, and "updates you haven't read" narrows to neither. Left
    // intersected, following the unread banner would land on a list with every resolved
    // request silently removed, while the number that sent you there counted them. `mine` and
    // the rest of the view survive: only this one clause is the bucket's to decide.
    ...(filters.attention ? { openOnly: undefined } : {}),

    attention: filters.attention,
    status: filters.status,
    minimumPriority: filters.minimumPriority,
    teamId: filters.teamId,
    siteId: filters.siteId,
    assigneeUserId: filters.assigneeUserId,
    requesterUserId: filters.requesterUserId,
    raisedFromUtc: startOfDayUtc(filters.raisedFrom, zone),
    raisedToUtc: endOfDayExclusiveUtc(filters.raisedTo, zone),
    sortBy,
    direction,
  };

  const { items, error, hasMore, isLoadingMore, loadMore, reload } = useTickets(query);

  /**
   * Sorts by a column, or flips the direction if it is already the sort.
   *
   * ⚠️ Only the columns the SERVER can sort by are clickable — `TicketSortField` is an
   * enum, so anything else cannot be expressed in a request. Subject is excluded there
   * deliberately: ordering PHI-capable text tells a caller something about its content.
   */
  const sortByColumn = (field: TicketSortField) =>
    void navigate({
      search: (current: TicketsSearch) => ({
        ...current,
        sort: field,
        dir:
          sortBy === field
            ? direction === "Ascending"
              ? ("desc" as const)
              : ("asc" as const)
            : firstDirection[field],
      }),
      replace: true,
    });

  /**
   * Switches to a saved view, and seeds its filters into the URL.
   *
   * ⚠️ Seeded rather than applied invisibly. A saved view's team, site, status and
   * priority are filters like any other, so they belong in the filter bar where they can
   * be seen and removed — a view that narrows the queue by a team the bar reports as "Any
   * team" is the same defect as a filter with no chip.
   *
   * The assignee and requester filters are cleared, because a saved view has nowhere to
   * store them: carrying the previous view's over would silently narrow this one.
   */
  function selectSavedView(savedView: SavedView) {
    setSavedId(savedView.id);

    void navigate({
      search: (current: TicketsSearch) => ({
        ...current,
        status: savedView.status ?? undefined,
        priority: savedView.minimumPriority ?? undefined,
        team: savedView.teamId ?? undefined,
        site: savedView.siteId ?? undefined,
        assignee: undefined,
        requester: undefined,

        // Cleared for the same reason: a saved view has nowhere to put a date range, so
        // carrying the previous view's over would silently narrow this one — and a range
        // saved months ago would go on hiding everything raised since.
        from: undefined,
        to: undefined,

        // And the attention bucket, which a saved view cannot store either. It is followed
        // from a home tile to answer one question; carrying it into a view somebody picked
        // afterwards would answer a different one.
        attention: undefined,
        sort: savedView.sortBy,
        dir: savedView.direction === "Ascending" ? ("asc" as const) : ("desc" as const),
      }),
      replace: true,
    });
  }

  async function saveCurrent() {
    const name = viewName.trim();
    if (!name) return;

    setSavingView(true);
    setSaveError(null);

    try {
      const created = await createSavedView({
        name,
        openOnly: "openOnly" in base ? Boolean(base.openOnly) : false,
        mine: "mine" in base ? Boolean(base.mine) : false,
        unassigned: "unassigned" in base ? Boolean(base.unassigned) : false,

        // The filters actually in effect, rather than the built-in view's defaults. These
        // used to be hard-coded null, so every saved view came back as the plain tab it
        // was saved from and the filtering was quietly discarded.
        teamId: filters.teamId ?? null,
        siteId: filters.siteId ?? null,
        status: filters.status ?? null,
        minimumPriority: filters.minimumPriority ?? base.minimumPriority ?? null,
        sortBy,
        direction,
        sortOrder: saved.length,
      });

      setSaved((current) => [...current, created]);
      setSavedId(created.id);
      setNamingView(false);
      setViewName("");
    } catch (e) {
      // The server explains a duplicate name or the per-user cap in terms a person can
      // act on, so it is shown rather than swallowed — and it is shown BESIDE THE BOX now
      // rather than in an OS alert, so the name that caused it is still on screen to edit.
      setSaveError(e instanceof ApiError ? e.message : "That view could not be saved.");
    } finally {
      setSavingView(false);
    }
  }

  async function removeView(id: string) {
    setDeletingView(null);

    try {
      await deleteSavedView(id);
      setSaved((current) => current.filter((s) => s.id !== id));
      if (savedId === id) setSavedId(null);
    } catch {
      // Nothing useful to say — the list reloads on the next visit either way.
    }
  }

  // Shown once anything is selected, so the checkboxes for the OTHER rows stop hiding
  // themselves mid-selection — reaching for the third row and finding nothing there is
  // the one moment the hover behaviour would be actively unhelpful.
  const selecting = selected.length > 0;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
      {/* Requests you already have open, so moving between three of them does not mean
          coming back here and searching for the one you had two minutes ago. */}
      <TicketTabs />

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tickets</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {items === null ? "Loading…" : `${items.length}${hasMore ? "+" : ""} shown`}
            {activeFilters > 0 && items !== null && " · filtered"}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Hidden on a phone, where the table is replaced by cards and there are no
              columns to configure. */}
          <div className="hidden md:block">
            <ColumnPicker visibleIds={visibleIds} onToggle={toggle} onReset={reset} />
          </div>

          <button
            type="button"
            onClick={() => void reload()}
            className="inline-flex h-9 items-center gap-2 rounded-lg border border-input px-3 text-sm transition hover:bg-accent"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden /> Refresh
          </button>
        </div>
      </header>

      {/* Says what is being hidden and offers one click out of it. Without this the queue
          is simply shorter than it was, with nothing on screen explaining why. */}
      {search.requester && requesterFilterApplied && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
          <UserSearch className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          <span className="min-w-0 flex-1">
            {filteringOwnRequests ? (
              <>
                Showing only requests <span className="font-medium">you raised</span>.
              </>
            ) : (
              <>
                Showing only requests raised by{" "}
                <span className="font-medium">{filteredPerson?.displayName}</span>.
              </>
            )}
          </span>

          <button
            type="button"
            onClick={() => setFilters({ ...filters, requesterUserId: undefined })}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-input bg-background px-2.5 text-xs transition hover:bg-accent"
          >
            <X className="h-3 w-3" aria-hidden />
            Clear filter
          </button>
        </div>
      )}

      {/* ⚠️ The link asked for one person's requests and the server declined to narrow, so
          this queue is everything the reader can see. Saying so is the whole point: the
          alternative — which is what shipped — was the banner above claiming a filter that
          was never applied, over a full queue.

          ⚠️ It states a fact about the READER and nothing about the id, exactly as the
          no-clearance empty state does. It does not say whether that id names a real person,
          or whether they have any requests, because the server declines to answer that and
          rebuilding the answer here would undo the reason it declines. */}
      {search.requester && filteredPerson === null && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-warning/40 bg-warning/5 px-3 py-2 text-sm">
          <UserSearch className="h-4 w-4 shrink-0 text-warning" aria-hidden />
          <span className="min-w-0 flex-1">
            This link filters by a person, which needs permission to look people up. Showing
            everything you can see instead.
          </span>

          <button
            type="button"
            onClick={() => setFilters({ ...filters, requesterUserId: undefined })}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-input bg-background px-2.5 text-xs transition hover:bg-accent"
          >
            <X className="h-3 w-3" aria-hidden />
            Dismiss
          </button>
        </div>
      )}

      {/* Horizontally scrollable on a phone rather than wrapping into three rows. */}
      <div className="mt-6 -mx-4 overflow-x-auto border-b border-border px-4 pb-3 sm:mx-0 sm:px-0">
        <div className="flex w-max items-center gap-2">
          {visibleViews.map((v) => (
            <button
              key={v}
              onClick={() => {
                void navigate({
                  search: (current: TicketsSearch) => ({ ...current, view: v }),
                  replace: true,
                });
                setSavedId(null);
              }}
              aria-current={view === v && !savedId ? "true" : undefined}
              className={cn(
                "h-8 shrink-0 rounded-lg px-3 text-sm transition",
                view === v && !savedId
                  ? "bg-secondary text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {v}
            </button>
          ))}

          {saved.length > 0 && <span className="h-5 w-px shrink-0 bg-border" aria-hidden />}

          {/* ⚠️ TWO SIBLING BUTTONS IN A WRAPPER, never one inside the other. This was a
              `span role="button" tabIndex=0` nested inside the tab's own `button` — nested
              interactive content, which is invalid HTML and leaves the inner control's
              keyboard and screen-reader behaviour undefined. Selecting a view and deleting
              it are two actions and now they are two elements. */}
          {saved.map((s) => (
            <span
              key={s.id}
              className={cn(
                "group inline-flex h-8 shrink-0 items-center rounded-lg text-sm transition",
                savedId === s.id
                  ? "bg-secondary text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <button
                type="button"
                onClick={() => selectSavedView(s)}
                aria-current={savedId === s.id ? "true" : undefined}
                className="h-8 rounded-l-lg pl-3 pr-1.5"
              >
                {s.name}
              </button>

              {/* ⚠️ ASKS FIRST, in place, turning into the question rather than opening a
                  dialog over the strip — the shape `ConfirmPanel` uses throughout
                  administration, where the standard is that nothing destructive happens on
                  one click. A saved view was the last thing in the product that did: an X a
                  few pixels from the name you meant to select, no undo behind it, and a set
                  of filters to rebuild from memory.

                  Nothing here is a control. The endpoint authorises the delete against the
                  caller's own views regardless; this exists so an authorised person does not
                  do an authorised thing by accident. */}
              {deletingView === s.id ? (
                <span className="inline-flex h-8 items-center gap-1 px-1">
                  <span className="text-xs">Delete?</span>
                  <button
                    type="button"
                    onClick={() => void removeView(s.id)}
                    className="h-6 rounded-md bg-destructive px-1.5 text-[11px] font-medium text-destructive-foreground transition hover:opacity-90"
                  >
                    Yes
                  </button>
                  {/* Focus lands on the one that does NOT act, as `ConfirmPanel` does: a
                      stray Enter after the question appears must not answer it. */}
                  <button
                    type="button"
                    autoFocus
                    onClick={() => setDeletingView(null)}
                    className="h-6 rounded-md border border-input bg-background px-1.5 text-[11px] transition hover:bg-accent"
                  >
                    No
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  aria-label={`Delete view ${s.name}`}
                  title={`Delete view ${s.name}`}
                  onClick={() => setDeletingView(s.id)}
                  className="h-8 rounded-r-lg pl-0.5 pr-2 opacity-0 transition group-hover:opacity-60 hover:!opacity-100 focus-visible:opacity-100"
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              )}
            </span>
          ))}

          {/* Saves the filters currently in effect. Nothing about the rows is stored —
              only the filter values, which the server applies on top of the same query
              filter and RLS as always. A saved view can never widen what you see. */}
          {namingView ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void saveCurrent();
              }}
              className="inline-flex h-8 shrink-0 items-center gap-1.5"
            >
              <label htmlFor="view-name" className="sr-only">
                Name this view
              </label>
              <input
                id="view-name"
                autoFocus
                value={viewName}
                maxLength={60}
                onChange={(e) => setViewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setNamingView(false);
                    setSaveError(null);
                  }
                }}
                placeholder="Name this view"
                className="h-8 w-44 rounded-lg border border-input bg-background px-2.5 text-sm transition focus:border-ring focus:outline-none"
              />
              <button
                type="submit"
                disabled={savingView || viewName.trim() === ""}
                className="inline-flex h-8 items-center rounded-lg bg-primary px-2.5 text-xs font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
              >
                {savingView ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setNamingView(false);
                  setSaveError(null);
                }}
                className="inline-flex h-8 items-center rounded-lg border border-input px-2.5 text-xs transition hover:bg-accent"
              >
                Cancel
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => {
                setViewName("");
                setSaveError(null);
                setNamingView(true);
              }}
              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-dashed border-input px-3 text-sm text-muted-foreground transition hover:text-foreground"
            >
              <Plus className="h-3 w-3" aria-hidden />
              Save view
            </button>
          )}
        </div>
      </div>

      {/* ⚠️ Said in the page, beside the box, where somebody about to name a view is looking.
          This warning used to travel inside the `window.prompt` string — an unstyled OS box
          that some browsers suppress entirely, in which case a view was saved silently
          narrower than the queue it was saved from and nobody was told. */}
      {namingView && unsaveableFilters.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          The {unsaveableFilters.join(" and ")} filter
          {unsaveableFilters.length === 1 ? "" : "s"} will not be saved with it — a saved view has
          nowhere to store {unsaveableFilters.length === 1 ? "it" : "them"}.
        </p>
      )}

      {saveError && (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {saveError}
        </p>
      )}

      <QueueFilters
        value={filters}
        onChange={setFilters}
        assigneeUnavailable={
          base.unassigned
            ? "This view already excludes everyone with a ticket assigned."
            : base.mine
              ? "This view is already scoped to your own tickets."
              : undefined
        }
      />

      {error && (
        <div
          role="alert"
          className="mt-4 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm"
        >
          {error}
        </div>
      )}

      {/* ⚠️ ABOVE THE LIST, sticky to the top of the viewport (D-99). It used to render after
          the table and the "Load more" button as `sticky bottom-0`, which put the only controls
          that act on a selection at the very bottom edge of the screen — under the browser's own
          chrome on a laptop and off the bottom entirely on a phone. Selecting rows and then
          being unable to find what to do with them is what this move fixes.

          ⚠️ Offers the actions; decides nothing. The server authorises each ticket individually
          and reports which ones it refused. */}
      <BulkActionBar
        selected={selected}
        onDone={() => void reload()}
        onClear={() => setSelected([])}
      />

      {items === null ? (
        <TicketSkeleton />
      ) : items.length === 0 ? (
        // ⚠️ Two different empty states, because they call for two different actions.
        // "Nothing matches your filters" is a thing you undo; "nothing is visible to you"
        // is a thing you ask an administrator about. Showing one for the other sends
        // people hunting for a request that was never lost.
        activeFilters > 0 ? (
          <NoFilterMatches onClear={() => setFilters({})} />
        ) : (
          <EmptyQueue view={view} />
        )
      ) : (
        <>
          {/* Cards on a phone. A nine-column table in a 390px viewport is unusable, and
              this screen is the one an on-call technician opens on their phone. */}
          <ul className="mt-4 space-y-2 md:hidden">
            {items.map((t) => (
              <li key={t.number}>
                <TicketCard ticket={t} />
              </li>
            ))}
          </ul>

          <div className="mt-4 hidden overflow-hidden rounded-2xl border border-border bg-card md:block">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr className="group/head">
                  <th className="w-10 px-4 py-3 text-left font-medium">
                    <input
                      type="checkbox"
                      aria-label="Select all on this page"
                      // Bounded by what is on screen, and the server caps a batch at 50
                      // regardless — "select all" must never mean "every ticket in the
                      // system", which is the shape of an accident nobody recovers from.
                      checked={items.length > 0 && selected.length === items.length}
                      onChange={(e) =>
                        setSelected(e.target.checked ? items.map((t) => t.number) : [])
                      }
                      className={cn(
                        "h-4 w-4 rounded border-input transition-opacity",
                        // ⚠️ Hidden, not absent. It stays in the DOM and in the tab order,
                        // and focusing it makes it visible — a control that only exists on
                        // hover is a control a keyboard user does not have.
                        selecting
                          ? "opacity-100"
                          : "opacity-0 focus-visible:opacity-100 group-hover/head:opacity-100",
                      )}
                    />
                  </th>
                  {columns.map((column) => {
                    const isSorted = column.sortBy === sortBy;

                    return (
                      <th
                        key={column.id}
                        style={{ width: widthOf(column) }}
                        aria-sort={
                          isSorted
                            ? direction === "Ascending"
                              ? "ascending"
                              : "descending"
                            : undefined
                        }
                        className="relative px-4 py-3 text-left font-medium"
                      >
                        {column.sortBy ? (
                          <button
                            type="button"
                            onClick={() => sortByColumn(column.sortBy!)}
                            className={cn(
                              "group/sort flex w-full min-w-0 items-center gap-1 text-left uppercase tracking-wider transition hover:text-foreground",
                              isSorted && "text-foreground",
                            )}
                          >
                            <span className="truncate">{column.label}</span>
                            {isSorted ? (
                              direction === "Ascending" ? (
                                <ArrowUp className="h-3 w-3 shrink-0" aria-hidden />
                              ) : (
                                <ArrowDown className="h-3 w-3 shrink-0" aria-hidden />
                              )
                            ) : (
                              // Only on hover: a sort marker on every column at rest makes
                              // the one that is actually sorted impossible to spot.
                              <ChevronsUpDown
                                className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover/sort:opacity-60"
                                aria-hidden
                              />
                            )}
                          </button>
                        ) : (
                          <span className="block truncate">{column.label}</span>
                        )}

                        <ColumnResizer
                          columnLabel={column.label}
                          width={widthOf(column)}
                          minWidth={column.minWidth}
                          onResize={(w) => resize(column.id, w)}
                        />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((t) => {
                  const isSelected = selected.includes(t.number);

                  return (
                    <tr
                      key={t.number}
                      className={cn(
                        "group/row transition hover:bg-secondary/30",
                        isSelected && "bg-primary/5",
                      )}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          aria-label={`Select ${t.number}`}
                          checked={isSelected}
                          onChange={(e) =>
                            setSelected((current) =>
                              e.target.checked
                                ? [...current, t.number]
                                : current.filter((n) => n !== t.number),
                            )
                          }
                          className={cn(
                            "h-4 w-4 rounded border-input transition-opacity",
                            isSelected || selecting
                              ? "opacity-100"
                              : "opacity-0 focus-visible:opacity-100 group-hover/row:opacity-100",
                          )}
                        />
                      </td>
                      {columns.map((column) => (
                        <td
                          key={column.id}
                          style={{ width: widthOf(column) }}
                          className={cn(
                            "px-4 py-3 align-top",
                            column.muted && "text-muted-foreground",
                          )}
                        >
                          {column.id === "number" ? (
                            <Link
                              to="/tickets/$id"
                              params={{ id: t.number }}
                              className="text-sm font-medium hover:text-primary"
                            >
                              {/* Marked where the eye already goes rather than in a column
                                  that can be switched off. The label travels with it below,
                                  so the dot is reinforcement and never the only signal. */}
                              {slaTone[t.slaState] && (
                                <span
                                  className={cn(
                                    "mr-1 inline-block h-1.5 w-1.5 rounded-full align-middle",
                                    slaTone[t.slaState]!.dot,
                                  )}
                                  aria-hidden
                                />
                              )}
                              #{t.displayNumber}
                            </Link>
                          ) : column.id === "subject" ? (
                            <Link
                              to="/tickets/$id"
                              params={{ id: t.number }}
                              // Wraps rather than truncating once the column is wide enough
                              // to be worth widening — showing more text is the whole reason
                              // someone drags a column out.
                              className="block break-words font-medium hover:text-primary"
                            >
                              {t.subject}
                            </Link>
                          ) : column.id === "status" && slaTone[t.slaState] ? (
                            // The SLA state rides with the status rather than taking a column
                            // of its own: they answer the same question, and a column can be
                            // switched off by whoever most needs to see this.
                            <span className="flex flex-wrap items-center gap-1.5">
                              <StatusPill status={t.status} />
                              <span
                                className={cn("text-[11px] font-medium", slaTone[t.slaState]!.text)}
                              >
                                {slaTone[t.slaState]!.label}
                              </span>
                            </span>
                          ) : column.id === "requester" ? (
                            // ⚠️ Marked beside the name, because VIP is a fact about the
                            // PERSON rather than about the request. That does mean the
                            // marker goes when the Requester column is switched off — an
                            // accepted trade: somebody who has hidden who raised things has
                            // hidden this too, and the VIP tab remains the reliable way to
                            // see these requests regardless of column layout.
                            <span className="flex flex-wrap items-center gap-1.5">
                              <span className="break-words">{t.requesterName}</span>
                              {t.isRequesterVip && <VipPill />}
                            </span>
                          ) : column.id === "priority" ? (
                            <PriorityPill p={t.priority} />
                          ) : column.id === "status" ? (
                            <StatusPill status={t.status} />
                          ) : column.id === "age" ? (
                            ageOf(t.createdAtUtc)
                          ) : column.id === "created" ? (
                            // ⚠️ The READER'S OWN zone, from the UTC value — three timezones
                            // across the sites, so the browser's zone is wrong for anybody
                            // reading away from theirs. Same rule as every other timestamp
                            // here; conversion at the presentation layer only.
                            <time dateTime={t.createdAtUtc} className="whitespace-nowrap text-xs">
                              {formatDateTime(t.createdAtUtc, zone)}
                            </time>
                          ) : column.id === "updated" ? (
                            ageOf(t.updatedAtUtc)
                          ) : column.id === "assignee" ? (
                            t.assigneeName ? (
                              <span className="truncate text-xs">{t.assigneeName}</span>
                            ) : (
                              <span className="text-xs text-muted-foreground">Unassigned</span>
                            )
                          ) : (
                            <span className="block break-words">{textValue(column, t)}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={isLoadingMore}
                className="h-10 rounded-lg border border-input px-4 text-sm transition hover:bg-accent disabled:opacity-50"
              >
                {isLoadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TicketCard({ ticket }: { ticket: TicketSummary }) {
  return (
    <Link
      to="/tickets/$id"
      params={{ id: ticket.number }}
      className="block rounded-xl border border-border bg-card p-3 transition active:bg-secondary/40"
    >
      <div className="flex items-start justify-between gap-3">
        <span className="flex items-center gap-1.5">
          {slaTone[ticket.slaState] && (
            <span
              className={cn("h-1.5 w-1.5 rounded-full", slaTone[ticket.slaState]!.dot)}
              aria-hidden
            />
          )}
          <span className="text-sm font-semibold">#{ticket.displayNumber}</span>
          {slaTone[ticket.slaState] && (
            <span className={cn("text-[11px] font-medium", slaTone[ticket.slaState]!.text)}>
              {slaTone[ticket.slaState]!.label}
            </span>
          )}
        </span>
        <PriorityPill p={ticket.priority} />
      </div>

      <p className="mt-1 line-clamp-2 font-medium">{ticket.subject}</p>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <StatusPill status={ticket.status} />
        <span className="flex items-center gap-1.5">
          {ticket.requesterName}
          {ticket.isRequesterVip && <VipPill />}
        </span>
        <span aria-hidden>·</span>
        <span>{ticket.teamName}</span>
        <span aria-hidden>·</span>
        <span>{ageOf(ticket.createdAtUtc)}</span>
        <span className="ml-auto">{ticket.assigneeName ?? "Unassigned"}</span>
      </div>
    </Link>
  );
}

/**
 * ⚠️ A label, never a control, and it decides nothing.
 *
 * VIP is routing and presentation metadata: it raises a request's visibility to the
 * service desk and grants its holder no access whatsoever. Nothing on this screen or
 * behind it branches on this value — the server never consults VIP status in an
 * authorization decision, and it must not start here.
 */
function VipPill() {
  return (
    <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning ring-1 ring-inset ring-warning/40">
      VIP
    </span>
  );
}

function StatusPill({ status }: { status: TicketSummary["status"] }) {
  return (
    <span className={cn("rounded-md px-2 py-0.5 text-xs font-medium", statusTone[status])}>
      {statusLabel[status]}
    </span>
  );
}

function TicketSkeleton() {
  return (
    <div className="mt-4 space-y-2" aria-busy="true" aria-label="Loading tickets">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="h-16 animate-pulse rounded-xl border border-border bg-card" />
      ))}
    </div>
  );
}

/**
 * ⚠️ An agent with no sensitivity clearance sees an empty queue that looks exactly like a
 * queue with nothing in it.
 *
 * Clearance comes from team membership, and both enforcement layers require a ticket's
 * classification to be cleared before any scope is considered. Someone on no team matches
 * nothing, ever — so "nothing here is visible to you" sent an administrator hunting for a
 * request that was never lost.
 *
 * Saying why discloses nothing. It reports a fact about the READER — one they can already
 * read on their own profile — and never anything about a ticket: not that one exists, not
 * how many, not in which category. A count of what was withheld would be a disclosure;
 * this is not.
 */
function EmptyQueue({ view }: { view: QueueView }) {
  const { user } = useSession();
  const hasNoClearance = (user?.clearedLevels.length ?? 0) === 0;

  return (
    <div className="mt-6 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border bg-card px-6 py-16 text-center">
      {hasNoClearance ? (
        <>
          <ShieldAlert className="h-9 w-9 text-warning" aria-hidden />
          <p className="text-sm font-medium">No requests are visible to you</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            You hold no sensitivity clearance, so no request can be shown to you. Clearance comes
            from membership of a team.
          </p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Ask an administrator to add you to a team.
          </p>
        </>
      ) : (
        <>
          <Inbox className="h-9 w-9 text-muted-foreground" aria-hidden />
          <p className="text-sm font-medium">Nothing in “{view}”</p>
          <p className="max-w-xs text-sm text-muted-foreground">
            Either the queue is clear, or nothing here is visible to you.
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Requester view
// ---------------------------------------------------------------------------

/**
 * ⚠️ THE TWO MIDDLE TABS EXIST SO THE HOME CARDS HAVE SOMEWHERE TO LAND. Home tells a
 * requester "one request is waiting on you" and "two are waiting for your feedback"; before
 * these, both statements linked to a screen that ignored the URL entirely and opened on
 * "Open", where the reader had to find those requests among everything else. A count somebody
 * cannot locate is worse than no count — it is a claim about their own data that the product
 * then declines to show them.
 *
 * "Needs you" is `AwaitingUser`, which is exactly what the home card counts. "To rate" is the
 * server's own `AwaitingRating` bucket, which reads the rating sweep's delay and horizon — so
 * the tab, the card and the email all describe one set of requests (D-63).
 */
const requestFilters = {
  Open: { openOnly: true },

  // ⚠️ NOT `openOnly`. An update on a request resolved yesterday is exactly the one somebody
  // has not read, and hiding it would leave the home banner counting requests this tab
  // refuses to show.
  Unread: { attention: "Unread" },
  "Needs you": { status: "AwaitingUser" },
  "To rate": { attention: "AwaitingRating" },
  Resolved: { status: "Resolved" },
  All: {},
} as const satisfies Record<string, TicketListQuery>;

type RequestFilter = keyof typeof requestFilters;

const REQUEST_FILTERS = Object.keys(requestFilters) as RequestFilter[];

/**
 * The three relationships a requester can have to a ticket they are allowed to read (D-47).
 *
 * ⚠️ THIS SCREEN WAS MIXING ALL THREE UNDER ONE HEADING, and that is the defect these tabs
 * fix rather than a feature they add. It sent no requester filter at all, on the reasoning
 * that Own scope already restricted the rows — which was true before participants (Q12.7)
 * and supervisor access (D-23) existed. Once they did, a page titled "Everything you've
 * sent to the service desk" was also listing tickets somebody had invited this person onto
 * and, for a manager, tickets their reports had raised. All three were legitimately
 * readable; none of them was what the heading said, and there was no way to tell them
 * apart.
 *
 * `raisedByMe` is now sent explicitly. It resolves to the caller's own id server-side, so the
 * first tab means what it says.
 *
 * ⚠️ NOT `mine`, which is what this sent and what made the tab empty for everybody. `mine`
 * narrows the ASSIGNEE — it is the agent queue's "My tickets", two hundred lines up. A
 * requester is not the assignee of their own request, so "My requests" matched nothing and
 * every ticket anybody raised vanished the moment they raised it. The request was readable
 * throughout: its notification link opened it, because a single-record read goes through the
 * query filter, which admits the requester's own row.
 */
const requestScopes = {
  "My requests": {
    query: { raisedByMe: true } satisfies TicketListQuery,
    blurb: "Everything you've sent to the service desk.",
    flag: null,
  },
  "Shared with me": {
    query: { sharedWithMe: true } satisfies TicketListQuery,
    blurb: "Requests a colleague added you to. You can read them and add comments.",
    flag: "hasSharedWithMe",
  },
  "My team": {
    query: { myReports: true } satisfies TicketListQuery,
    blurb: "Requests raised by people who report to you and have chosen to share them.",
    flag: "hasTeamRequests",
  },
} as const satisfies Record<
  string,
  { query: TicketListQuery; blurb: string; flag: keyof SessionTicketTabs | null }
>;

type RequestScope = keyof typeof requestScopes;

const REQUEST_SCOPES = Object.keys(requestScopes) as RequestScope[];

function MyRequests() {
  // ⚠️ THE URL, not local state, because the home screen links here. This was `useState`, so
  // every card on home opened the "Open" tab regardless of what it had just counted — the
  // reader was told a number and then shown a different list. `view` is the same parameter
  // the agent queue uses for its tabs, so one screen's link cannot mean something else here.
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const filter: RequestFilter = search.tab ?? "Open";

  /**
   * ⚠️ In the URL for the same reason the tab is, which the first pass at this missed: it
   * was left as `useState` directly beside a comment explaining why that was wrong. A
   * reload, the back button or a link shared with a colleague all dropped somebody back to
   * "My requests" — so "look at the one shared with me" was a sentence with no link behind
   * it, and refreshing while reading a report's request silently changed which list was on
   * screen.
   *
   * ⚠️ Rendering only, exactly like the tab. Each scope resolves to a flag the SERVER
   * evaluates from the session — never an id from here — so forcing an unfamiliar value in
   * the URL yields the default list, and forcing a scope somebody has no relationship for
   * yields an empty page rather than anybody else's rows.
   */
  const scope: RequestScope =
    search.scope && search.scope in requestScopes ? search.scope : "My requests";

  /** Replaces rather than pushes: switching tabs is not a history trail. */
  const setFilter = (next: RequestFilter) =>
    void navigate({
      search: (current: TicketsSearch) => ({
        ...current,
        tab: next === "Open" ? undefined : next,
      }),
      replace: true,
    });

  const setScope = (next: RequestScope) =>
    void navigate({
      search: (current: TicketsSearch) => ({
        ...current,
        scope: next === "My requests" ? undefined : next,
      }),
      replace: true,
    });

  /** The box's own text, applied only on submit. The applied term lives in the URL. */
  const [requestSearch, setRequestSearch] = useState(search.q ?? "");

  useEffect(() => setRequestSearch(search.q ?? ""), [search.q]);

  const { user } = useSession();
  const tabs = user?.ticketTabs;

  // Drawn only where the relationship exists. See relationshipViews on the agent queue for
  // why an always-visible empty tab is the worse option — and note this decides rendering
  // only: every scope below resolves server-side from the session.
  const scopes = (Object.keys(requestScopes) as RequestScope[]).filter((s) => {
    const flag = requestScopes[s].flag;
    return flag === null || tabs?.[flag] === true;
  });

  const active = requestScopes[scope];

  // ⚠️ THE UNREAD TAB IGNORES THE SCOPE, and the scope strip is hidden while it is open.
  //
  // Home counts unread across every request the reader can see — their own, ones a colleague
  // invited them onto, ones a report shared. Intersecting that with "raised by me" would leave
  // the banner saying two and the tab showing one, which is the defect this tab was added to
  // fix rather than a smaller version of it.
  //
  // It is safe to drop because the unread filter is already the tightest personal scope there
  // is: it selects on notification rows addressed to this caller, so it cannot reach a request
  // the three scope tabs would have excluded.
  const scopeless = filter === "Unread";

  const { items, error, hasMore, isLoadingMore, loadMore } = useTickets({
    ...(scopeless ? {} : active.query),
    ...requestFilters[filter],
    ...(search.q ? { search: search.q } : {}),
    sortBy: "CreatedAt",
    direction: "Descending",
  });

  /**
   * Which of these requests have updates the reader has not seen.
   *
   * ⚠️ THE DASHBOARD'S OWN ROWS, not a second count. Home says "3 requests have updates you
   * haven't read" and the bell agrees with it, because both read `NotificationMessages` — one
   * row per recipient per event, composed after the authorization check (D-17). Reading the
   * same payload here means a third surface cannot disagree with the other two, and it needs
   * no new field on the list response.
   *
   * ⚠️ Counting unread COMMENTS instead would count rows the reader may not be allowed to
   * read — a badge of 3 on a request with two public replies and one internal note tells the
   * requester an internal note exists, which is D2 as a number rather than as a payload.
   *
   * ⚠️ Null is "we do not know", never "nothing": in-app notifications may be switched off,
   * and `unreadFor` answers 0 for anything the server did not name. A badge that cannot load
   * is a badge that is not drawn — it must never take this list down with it.
   */
  const [unread, setUnread] = useState<Dashboard | null>(null);

  useEffect(() => {
    let cancelled = false;

    void getDashboard()
      .then((d) => !cancelled && setUnread(d))
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
      {/* ⚠️ No "New request" button here. The app bar carries "New ticket" on every screen
          including this one, and two buttons a few inches apart that go to the same place
          read as two different things — the question "which one do I want?" is worse than
          the second button is useful. */}
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Requests</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {scopeless
            ? "Every request you can see that has an update you haven't read."
            : active.blurb}
        </p>
      </header>

      {/* Only when there is a choice to make. One tab is not a tab strip, and drawing it
          for the many people who are on nobody else's ticket is a control that never does
          anything.

          ⚠️ And never on the unread tab, which covers all three at once — a strip whose
          buttons change nothing is worse than no strip. */}
      {scopes.length > 1 && !scopeless && (
        <nav
          aria-label="Which requests"
          className="mt-6 -mx-4 overflow-x-auto border-b border-border px-4 sm:mx-0 sm:px-0"
        >
          <div className="flex w-max items-center gap-1">
            {scopes.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setScope(s)}
                aria-current={scope === s ? "page" : undefined}
                className={cn(
                  "h-9 rounded-t-lg border-b-2 px-3 text-sm font-medium transition",
                  scope === s
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {s}
              </button>
            ))}
          </div>
        </nav>
      )}

      {/* ⚠️ A requester needs this as much as the desk does, and arguably more: they have no
          filter bar, no saved views and no queue to scan. "I reported this months ago" was
          answerable only by paging through All until it appeared.

          Searches on submit, never while typing — every match files a PHI read against the
          searcher's name (D-33's rule, and see `SubjectSearch`). Subjects only. */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const next = requestSearch.trim();
          if (next.length > 0 && next.length < MINIMUM_SEARCH_LENGTH) return;

          void navigate({
            search: (current: TicketsSearch) => ({ ...current, q: next || undefined }),
            replace: true,
          });
        }}
        className="relative mt-6"
      >
        <label htmlFor="request-search" className="sr-only">
          Search your requests
        </label>
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <input
          id="request-search"
          value={requestSearch}
          maxLength={200}
          onChange={(e) => setRequestSearch(e.target.value)}
          placeholder="Search what you wrote in the subject, then press Enter"
          className="h-10 w-full rounded-lg border border-input bg-background pl-9 pr-9 text-base transition focus:border-ring focus:outline-none md:text-sm"
        />
        {requestSearch && (
          <button
            type="button"
            aria-label="Clear the search"
            onClick={() => {
              setRequestSearch("");
              void navigate({
                search: (current: TicketsSearch) => ({ ...current, q: undefined }),
                replace: true,
              });
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition hover:text-foreground"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </form>

      <div className="mt-4 inline-flex rounded-lg border border-border bg-card p-1">
        {(Object.keys(requestFilters) as RequestFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            aria-current={filter === f ? "true" : undefined}
            className={cn(
              "h-8 rounded-md px-4 text-sm font-medium transition",
              filter === f
                ? "bg-secondary text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {f}
          </button>
        ))}
      </div>

      {error && (
        <div
          role="alert"
          className="mt-4 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm"
        >
          {error}
        </div>
      )}

      {items === null ? (
        <TicketSkeleton />
      ) : items.length === 0 ? (
        <div className="mt-6 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border bg-card px-6 py-16 text-center">
          {/* ⚠️ Worded per tab. "You're all caught up — start a new request" is right for
              your own queue and wrong for the other two: nobody empties "Shared with me" by
              raising a ticket, and telling a manager to file one because their reports have
              not is a non-sequitur. An empty state that answers a question nobody asked is
              how somebody concludes the screen is broken. */}
          {scope !== "My requests" ? (
            <>
              <Inbox className="h-9 w-9 text-muted-foreground" aria-hidden />
              <p className="text-sm font-medium">Nothing here</p>
              <p className="max-w-xs text-sm text-muted-foreground">
                {scope === "Shared with me"
                  ? "When a colleague adds you to one of their requests, it will show up here."
                  : "Nothing open from your team right now. You'll only see requests from people who have chosen to share them."}
              </p>
            </>
          ) : filter === "Open" ? (
            <>
              <CheckCircle2 className="h-9 w-9 text-success" aria-hidden />
              <p className="text-sm font-medium">No open requests</p>
              <p className="max-w-xs text-sm text-muted-foreground">
                You're all caught up. Need something? Start a new request and we'll take it from
                here.
              </p>
            </>
          ) : (
            <>
              <Inbox className="h-9 w-9 text-muted-foreground" aria-hidden />
              <p className="text-sm text-muted-foreground">Nothing here yet.</p>
            </>
          )}
        </div>
      ) : (
        <>
          <ul className="mt-4 space-y-2">
            {items.map((t) => (
              <li key={t.number}>
                <Link
                  to="/tickets/$id"
                  params={{ id: t.number }}
                  className="flex items-start justify-between gap-4 rounded-xl border border-border bg-card p-4 transition hover:border-primary/40"
                >
                  <div className="min-w-0">
                    <p className="line-clamp-2 font-medium">{t.subject}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {/* The readable number, as the agent queue and every email show it.
                          This used to print the opaque TF- number, so a requester reading
                          their own list and an agent reading the queue were quoting two
                          different identifiers for the same request to each other. */}
                      #{t.displayNumber} · {t.teamName} · opened {ageOf(t.createdAtUtc)} ago
                    </p>
                  </div>

                  {/* ⚠️ WHICH request has news. Home tells somebody two of their requests
                      have updates and this list is where they come to find out which — and
                      it said nothing, on every tab including the one added specifically to
                      hold them. A count the product declines to locate is worse than no
                      count, which is the whole of D-70. */}
                  <span className="flex shrink-0 items-center gap-2">
                    <UnreadBadge count={unreadFor(unread, t.number)} />
                    <StatusPill status={t.status} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {hasMore && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={isLoadingMore}
                className="h-10 rounded-lg border border-input px-4 text-sm transition hover:bg-accent disabled:opacity-50"
              >
                {isLoadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
